import { Box, IconButton, Typography, useTheme, Badge } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Avatar from '@mui/material/Avatar';
import BackButton from 'components/elements/BackButton';
import Notifications from 'components/notification';
import jwt_decode from 'jwt-decode';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserById } from 'service/UserService';
import {
  setEmpId,
  setFirstName,
  setLastName,
  setOrgId,
  setProfileIamge,
} from 'store/onboarding';
import { type RootState } from 'store/rootReducer';
import { colorTheme } from 'theme';
import { type IErrorResponse, type ICustomerResponse } from 'types';
import { pagesConstantList } from 'routes/Constant';
import { getMasterFundsList } from 'service/OrderService';
import { setAllMasterFunds } from 'store/fund';
import { emailDetails } from 'service/AuthService';
import { type ResponseData } from 'pages/Auth/VerifyOtp';
import { setUserDetails } from 'store/auth';
import { setConfiguration } from 'store/configuration';
import { setSnackBarFailed } from 'store/snackBar';
import { setShowFlexTokenExpiryInfo } from 'store/notifications';
import { getEconomicStatusAndOccupation } from 'service/EmployerService';
import { setEconomicStatusAndOccupation } from 'store/employeeDetails';
import { getAllActiveConfiguration } from 'service/ConfigurationService';
import { getNoOfDayDiff } from 'utils/GeneralUtil';

const TopBar = (): JSX.Element => {
  const { firstName, lastName, profileImage } = useSelector(
    (state: RootState) => state.onboarding,
  );
  const { unreadCount } = useSelector(
    (state: RootState) => state.notifications,
  );
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const credentials = JSON.parse(
    sessionStorage.getItem('credentials') as string,
  );
  const decoded: { sub?: number } = jwt_decode(credentials?.access_token);
  const dispatch = useDispatch();
  const { roles } = useSelector((state: RootState) => state.auth);
  const { configuration } = useSelector(
    (state: RootState) => state.configuration,
  );

  const empId: number | undefined = decoded?.sub;
  const styles = {
    main: {
      display: 'flex',
      justifyContent: 'space-between',
      bgcolor: colors.primaryBg[900],
      boxShadow: 2,
      padding: 2,
    },
    isProfilAavatar: {
      width: 32,
      height: 32,
      gap: 10,
    },
    notProfileAvatar: {
      width: 32,
      height: 32,
      bgcolor: '#B5DFC8',
      color: '#202020',
      fontSize: 16,
      fontFamily: 'Inter',
    },
    profileTypehography1: {
      component: 'h5',
      variant: 'h5',
      color: '#202020',
      fontWeight: 500,
      fontSize: 14,
    },
    profileTypehography2: {
      fontFamily: 'Inter',
      color: colors.gray[500],
      fontWeight: 400,
      fontSize: 12,
    },
    profileAvatarBox: {
      display: 'flex',
    },
    innerBoxStyle: {
      display: 'flex',
      gap: 1,
    },
  };
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const userDetails = async (): Promise<void> => {
    if (empId) {
      try {
        const res = (await getUserById()) as ICustomerResponse;
        if (res.basicInfo?.firstName) {
          dispatch(setFirstName(res.basicInfo?.firstName));
        }
        if (res.basicInfo?.lastName) {
          dispatch(setLastName(res.basicInfo?.lastName));
        }
        if (res.basicInfo?.empId) {
          dispatch(setEmpId(res.basicInfo?.empId));
        }
        if (res.basicInfo?.organizationId) {
          dispatch(setOrgId(res?.basicInfo?.organizationId));
        }
        if (res.basicInfo?.profileImage) {
          dispatch(setProfileIamge(res.basicInfo?.profileImage));
        }
      } catch (err) {
        throw new Error(`${String(err)}`);
      }
    }
  };

  useEffect(() => {
    if (empId) {
      void userDetails();
    }
  }, [empId]);

  useEffect(() => {
    const flexWebToken = configuration?.find(
      (a) => a.variable === 'FLEX_WEB_SERVICE_TOKEN',
    );
    if (flexWebToken?.validThru) {
      const diff = getNoOfDayDiff(flexWebToken.validThru);

      if (diff < 45) {
        dispatch(
          setShowFlexTokenExpiryInfo({
            show: true,
            days: diff,
          }),
        );
      } else {
        dispatch(
          setShowFlexTokenExpiryInfo({
            show: false,
            days: 0,
          }),
        );
      }
    }
  }, [configuration]);

  const handleEmailDetails = async (): void => {
    await emailDetails('')
      .then((data: unknown) => {
        const responseData = data as ResponseData;
        dispatch(setUserDetails(responseData?.result));
      })
      .catch((err: IErrorResponse) => {
        dispatch(
          setSnackBarFailed({ snackBarMessage: err.response.data.message }),
        );
      });
  };

  useEffect(() => {
    void getAllMasterData();
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    void handleEmailDetails();
  }, []);

  useEffect(() => {
    if (roles?.includes('mm_employee')) {
      void getData();
    }
  }, [roles]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getData = async () => {
    const configs: unknown = await getAllActiveConfiguration();
    dispatch(setConfiguration(configs?.data || []));
  };

  const getAllMasterData = async (): Promise<void> => {
    try {
      const result = await getMasterFundsList();
      dispatch(setAllMasterFunds(result?.data));

      const resultData = await getEconomicStatusAndOccupation();
      dispatch(setEconomicStatusAndOccupation(resultData));
    } catch (error) {
      console.log(error);
    }
  };

  const displayUserRoles = (roles: string[] | null | string): string => {
    if (!roles) return '';
    if (roles.includes('organization_admin')) {
      return 'Employee Admin';
    } else if (roles.includes('mm_employee')) {
      return 'Super Admin';
    } else if (roles.includes('ceo')) {
      return 'Approver Admin';
    }
    return 'Admin';
  };

  return (
    <>
      <Box sx={styles.main}>
        <Box>
          <BackButton />
        </Box>
        <Box sx={styles.innerBoxStyle}>
          {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            (roles?.includes(pagesConstantList.ceo) ||
              roles?.includes(pagesConstantList.mmEmployee)) && (
              <IconButton onClick={handleClick}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )
          }
          <Notifications
            anchorEl={anchorEl}
            handleClose={handleClose}
            open={open}
          />
          <Box sx={styles.profileAvatarBox}>
            {profileImage ? (
              <Avatar
                sx={styles.isProfilAavatar}
                alt="img"
                src={profileImage}
              />
            ) : (
              <Avatar sx={styles.notProfileAvatar}>
                {firstName.slice(0, 1) + lastName.slice(0, 1)}
              </Avatar>
            )}
            <Box marginLeft={1} />
            <Box>
              <Typography
                component="h5"
                variant="h5"
                sx={styles.profileTypehography1}
              >
                {`${firstName} ${lastName}`}
              </Typography>
              <Typography
                component="h5"
                variant="h5"
                sx={styles.profileTypehography2}
              >
                {displayUserRoles(roles)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TopBar;
